// packages/web-ui/src/state/diagnostics.ts
//
// Story W3-S3 AC #9 — `diagnostics` zustand slice + selector.
//
// Holds the most-recent SSE `validation:result` payload per workspace path so
// the file-viewer can re-run the linter when the open file's diagnostics
// change externally (file watcher → server validation → SSE fan-out).
//
// The slice is independent of the W2-S5 `sse` connection-lifecycle slice; the
// `<SseProvider>` consumes incoming `validation:result` envelopes via
// `recordValidationResult(...)`. The W3-S3 `useValidationResultForPath()` hook
// reads through this slice and triggers `forceLinting(view)`.
//
// Vanilla store factory (mirrors `state/save-gate.ts` / `state/capabilities.ts`)
// so component tests can inject an isolated store. Multiple files-of-interest
// share one store: each path is keyed independently — no cross-contamination
// between concurrent file-viewers (only one is open at a time today, but the
// API leaves room for multi-pane in the future).

import { createStore, type StoreApi } from 'zustand/vanilla';
import type { Diagnostic } from '@tessa-lean/base/api-schemas/validation.js';

/**
 * Per-path validation result — the SSE event payload projected onto the
 * SPA shape. `version` increments on every recordValidationResult call so
 * React effects can fire even when diagnostics are byte-identical (the
 * effect drives `forceLinting`; identity is checked downstream by CM6).
 */
export interface ValidationResultEntry {
  /** Workspace-relative path. */
  path: string;
  /** LSP-shape diagnostics. */
  diagnostics: Diagnostic[];
  /** Wall-clock ISO 8601 of the SSE event arrival. */
  receivedAt: string;
  /**
   * Monotonic counter — bumped on every record, used by React effects as
   * a dependency so identical diagnostic arrays still trigger re-runs.
   */
  version: number;
}

export interface DiagnosticsSliceState {
  /** path → most-recent validation result. */
  resultsByPath: Record<string, ValidationResultEntry>;
}

export interface DiagnosticsSliceActions {
  /** Record a new validation result; replaces any prior entry for `path`. */
  recordValidationResult: (input: { path: string; diagnostics: Diagnostic[] }) => void;
  /** Drop the entry for `path`. Idempotent. */
  clearValidationResult: (path: string) => void;
  /** Test/debug helper — wipe all entries. */
  _resetDiagnostics: () => void;
}

export type DiagnosticsSlice = DiagnosticsSliceState & DiagnosticsSliceActions;

export const DIAGNOSTICS_INITIAL_STATE: DiagnosticsSliceState = {
  resultsByPath: {},
};

/**
 * Vanilla zustand store factory — single source of truth for SSE-driven
 * validation results across the SPA. Composes into the root store at
 * integration time; tests construct a fresh isolated store per case.
 */
export function createDiagnosticsStore(): StoreApi<DiagnosticsSlice> {
  let nextVersion = 1;
  return createStore<DiagnosticsSlice>((set) => ({
    ...DIAGNOSTICS_INITIAL_STATE,

    recordValidationResult: ({ path, diagnostics }) => {
      const version = nextVersion;
      nextVersion += 1;
      const entry: ValidationResultEntry = {
        path,
        diagnostics,
        receivedAt: new Date().toISOString(),
        version,
      };
      set((s) => ({
        ...s,
        resultsByPath: { ...s.resultsByPath, [path]: entry },
      }));
    },

    clearValidationResult: (path) => {
      set((s) => {
        if (s.resultsByPath[path] === undefined) return s;
        const next: Record<string, ValidationResultEntry> = { ...s.resultsByPath };
        delete next[path];
        return { ...s, resultsByPath: next };
      });
    },

    _resetDiagnostics: () => {
      nextVersion = 1;
      set((s) => ({ ...s, resultsByPath: {} }));
    },
  }));
}

// ─── Module-singleton store ──────────────────────────────────────────────

let defaultStoreSingleton: StoreApi<DiagnosticsSlice> | null = null;

/**
 * Shared default store. Components within the SPA's render tree subscribe
 * to this singleton via `useValidationResultForPath()` (see
 * `hooks/useValidationResultForPath.ts`). Tests should always pass an
 * explicit store rather than depend on this singleton.
 */
export function getDefaultDiagnosticsStore(): StoreApi<DiagnosticsSlice> {
  defaultStoreSingleton ??= createDiagnosticsStore();
  return defaultStoreSingleton;
}

/** Test-only — wipes the singleton so the next call lazy-rebuilds it. */
export function _resetDefaultDiagnosticsStoreForTests(): void {
  defaultStoreSingleton = null;
}

// ─── Selectors ───────────────────────────────────────────────────────────

export function selectValidationResultForPath(
  state: DiagnosticsSliceState,
  path: string,
): ValidationResultEntry | null {
  return state.resultsByPath[path] ?? null;
}
